import { useEffect, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Typography, Grid, Box } from '@mui/material';
import { GRID_CHECKBOX_SELECTION_COL_DEF } from '@mui/x-data-grid-pro';
import Iconify from '../../../../../reusable-components/iconify';
import { CART } from '../../../../../reusable-components/datagrid/cartColumns';
import CustomDataGrid from '../../../../../reusable-components/datagrid/CustomDataGrid';
import CartTableToolbar from './CartTableToolbar';
import { QuantityField, UOMField } from './CartTableCols';

CartTable.propTypes = {
  cart: PropTypes.object,
  showTableCheckbox: PropTypes.bool,
  setBulkDeleteItems: PropTypes.func,
  singleCart: PropTypes.bool,
  canViewCart: PropTypes.bool,
  facilityId: PropTypes.string,
};

export default function CartTable({ cart, singleCart, showTableCheckbox, canViewCart, setBulkDeleteItems, facilityId }) {
  const [allExpanded, setAllExpanded] = useState(singleCart);
  const [itemsSelected, setItemsSelected] = useState([]);
  const [pinnedColumns, setPinnedColumns] = useState({
    left: [GRID_CHECKBOX_SELECTION_COL_DEF.field, 'picture'],
    right: ['vendor', 'total', 'quantity', 'actions'],
  });

  useEffect(() => { setBulkDeleteItems(itemsSelected); }, [setBulkDeleteItems, itemsSelected]);
  useEffect(() => { setAllExpanded(singleCart); }, [singleCart]);
  useEffect(() => { setItemsSelected([]); }, [showTableCheckbox]);

  const tableToolbar = useCallback(() => <CartTableToolbar {...{ cart, facilityId, showTableCheckbox, canViewCart }} />, [cart, showTableCheckbox, canViewCart, facilityId]);
  const cols = useMemo(() => [...CART,
  {
    field: 'uomType',
    headerName: 'UOM',
    renderCell: (params) => <UOMField params={params} />,
    width: 90,
    type: 'customSelect',
  },
  {
    field: 'quantity',
    headerName: 'Qty',
    width: 150,
    renderCell: (params) => <QuantityField params={params} canViewCart={canViewCart} showTableCheckbox={showTableCheckbox} />, 
  }].filter(col => col), [canViewCart, showTableCheckbox]);

  return <>
    <Grid item xs={12} sx={{ height: allExpanded ? "calc(100vh - 250px)" : 300, minHeight: 130 }}>
      <CustomDataGrid
        gridId="orders-new-order-cart"
        boxSX={{ width: '100%', height: allExpanded ? "calc(100vh - 250px)" : 300 }}
        tableBoxSX={{ height: "calc(100% - 50px)" }}
        data={cart.items}
        gridColumns={cols}
        pinnedColumns={pinnedColumns}
        onPinnedColumnsChange={(params) => {
          setPinnedColumns(params.pinnedColumns);
        }}
        dontGetRowHeight
        disableCellHighlight
        scrollbarHeight={null}        
        checkboxSelection={showTableCheckbox}
        checkboxTooltipText="The vendor doesn't allow changes to punchout orders. If you delete this item the whole punchout will be removed."
        checkboxTooltipCheck={(row) => row.punchoutLocked}      
        isRowSelectable={(params) => !params.row.punchoutLocked}
        onRowSelectionModelChange={(newSelection) => {
          if (newSelection.length === 0) setItemsSelected(newSelection);
          else if (newSelection.length < itemsSelected.length) {
            const addItem = itemsSelected.find(item => !newSelection.includes(item));
            const addPunchout = cart.items.find(item => item.punchoutId > 0 && item.punchoutLocked && item.id === addItem)?.punchoutId;
            setItemsSelected(addPunchout ? cart.items.filter(item => newSelection.includes(item.id) && item.punchoutId !== addPunchout).map(item => item.id) : newSelection);
          } else {
            const removePunchouts = cart.items.filter(item => item.punchoutId > 0 && item.punchoutLocked && newSelection.includes(item.id)).map(item => item.punchoutId);
            setItemsSelected(removePunchouts.length === 0 ? newSelection : Array.from(new Set([...newSelection, ...cart.items.filter(item => removePunchouts.includes(item.punchoutId)).map(item => item.id)])));
          }
        }}
        rowSelectionModel={itemsSelected}
        CustomLeftToolbar={tableToolbar}
      />
    </Grid>
    {!singleCart && <Box sx={{ justifyContent: "flex-end", display: "flex", textDecoration: 'underline', '&:hover': { cursor: 'pointer' }, my: 1 }}
      onClick={() => setAllExpanded(!allExpanded)}>
      <Typography variant="subtitle2">{allExpanded ? 'Close' : 'View full grid'} </Typography>
      <Iconify icon={allExpanded ? 'mdi:chevron-up' : 'mdi:chevron-down'} width={16} height={16} />
    </Box>}
  </>;
}
